import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  alpha,
  IconButton,
  Tooltip,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  AspectRatio as ResizeIcon,
} from '@mui/icons-material';

/**
 * Genel amaçlı sürüklenebilir ve yeniden boyutlandırılabilir popup.
 *
 * Props:
 *  open, onClose, title, icon, maxWidth ('xs'|'sm'|'md'|'lg'|'xl'),
 *  primaryColor, children
 */
function DraggableResizablePopup({
  open,
  onClose,
  title = '',
  subtitle = '',
  icon: HeaderIcon,
  maxWidth = 'lg',
  defaultWidth,
  defaultHeight,
  primaryColor,
  headerActions,
  footerActions,
  zIndex: zIndexProp,
  children,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pc = primaryColor || theme.palette.primary.main;

  const dialogRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(isMobile);
  const [position, setPosition] = useState(null);
  const [size, setSize] = useState(null);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => { if (isMobile) setIsFullScreen(true); }, [isMobile]);

  const getInitialSize = useCallback(() => {
    if (isMobile) return { width: window.innerWidth, height: window.innerHeight };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // defaultWidth / defaultHeight prop override varsa onu kullan
    if (defaultWidth || defaultHeight) {
      return {
        width: Math.min(defaultWidth || 900, vw - 40),
        height: Math.min(defaultHeight || Math.round(vh * 0.85), vh - 80),
      };
    }
    const map = {
      xs: { width: Math.min(550, vw - 40), height: Math.min(620, vh - 80) },
      sm: { width: Math.min(700, vw - 40), height: Math.min(740, vh - 80) },
      md: { width: Math.min(880, vw - 40), height: Math.min(Math.round(vh * 0.85), vh - 80) },
      lg: { width: Math.min(1200, vw - 40), height: Math.min(Math.round(vh * 0.85), vh - 80) },
      xl: { width: Math.min(1280, vw - 40), height: Math.min(Math.round(vh * 0.85), vh - 80) },
    };
    return map[maxWidth] || map.md;
  }, [maxWidth, defaultWidth, defaultHeight, isMobile]);

  // Centre when first opened
  useEffect(() => {
    if (open && !position && !isFullScreen) {
      const s = getInitialSize();
      setSize(s);
      setPosition({
        x: Math.max(50, (window.innerWidth - s.width) / 2),
        y: Math.max(50, (window.innerHeight - s.height) / 2),
      });
    }
  }, [open, position, isFullScreen, getInitialSize]);

  // Toggle fullscreen
  useEffect(() => {
    if (isFullScreen) { setPosition(null); setSize(null); }
    else if (open) {
      const s = getInitialSize();
      setSize(s);
      setPosition({
        x: Math.max(50, (window.innerWidth - s.width) / 2),
        y: Math.max(50, (window.innerHeight - s.height) / 2),
      });
    }
  }, [isFullScreen, open, getInitialSize]);

  /* ── Drag ── */
  const handleDragStart = useCallback((e) => {
    if (isFullScreen) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, posX: position?.x || 0, posY: position?.y || 0 };
  }, [isFullScreen, position]);

  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPosition({
      x: Math.max(0, Math.min(window.innerWidth - (size?.width || 400), dragStart.current.posX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - 50, dragStart.current.posY + dy)),
    });
  }, [isDragging, size]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  /* ── Resize ── */
  const handleResizeStart = useCallback((e) => {
    if (isFullScreen) return;
    e.preventDefault(); e.stopPropagation();
    setIsResizing(true);
    resizeStart.current = { x: e.clientX, y: e.clientY, width: size?.width || 600, height: size?.height || 500 };
  }, [isFullScreen, size]);

  const handleResize = useCallback((e) => {
    if (!isResizing) return;
    const dx = e.clientX - resizeStart.current.x;
    const dy = e.clientY - resizeStart.current.y;
    setSize({
      width: Math.max(350, Math.min(window.innerWidth - (position?.x || 0) - 20, resizeStart.current.width + dx)),
      height: Math.max(300, Math.min(window.innerHeight - (position?.y || 0) - 20, resizeStart.current.height + dy)),
    });
  }, [isResizing, position]);

  const handleResizeEnd = useCallback(() => setIsResizing(false), []);

  /* ── Global listeners ── */
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => { window.removeEventListener('mousemove', handleDrag); window.removeEventListener('mouseup', handleDragEnd); };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => { window.removeEventListener('mousemove', handleResize); window.removeEventListener('mouseup', handleResizeEnd); };
    }
  }, [isResizing, handleResize, handleResizeEnd]);

  /* ── ESC tuşu ile kapatma (draggable mod için — fullscreen Dialog zaten destekler) ── */
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  /* ── Shared header ── */
  const headerContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {HeaderIcon && <HeaderIcon sx={{ color: pc, fontSize: 22 }} />}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );

  /* ── Shared footer ── */
  const footer = (
    <Button
      startIcon={<CloseIcon />}
      onClick={onClose}
      variant="contained"
      size="small"
      sx={{
        bgcolor: pc,
        color: '#fff',
        borderRadius: 2,
        textTransform: 'none',
        '&:hover': { bgcolor: alpha(pc, 0.85) }
      }}
    >
      Kapat
    </Button>
  );

  /*
   * Tam ekran / draggable modları tek bir ağaçta (aynı Paper + children konumu)
   * tutuluyor; sadece stil (konum/boyut) değişiyor. Böylece "Tam Ekran" butonuna
   * basıldığında children (form içeriği) unmount/remount olmuyor ve içerideki
   * veri tekrar çekilmiyor.
   */
  return (
    <>
      {/* Backdrop - sadece draggable modda tıklayınca kapatır */}
      {!isFullScreen && (
        <Box onClick={onClose} sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(0,0,0,0.5)', zIndex: (zIndexProp ?? 1300) - 1,
        }} />
      )}

      <Paper ref={dialogRef} elevation={24} sx={{
        position: 'fixed',
        ...(isFullScreen
          ? { top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }
          : {
            left: position?.x ?? '50%',
            top: position?.y ?? '50%',
            transform: position ? 'none' : 'translate(-50%, -50%)',
            width: size?.width ?? 600,
            height: size?.height ?? 500,
            maxWidth: '95vw', maxHeight: '95vh',
          }),
        borderRadius: isFullScreen ? 0 : 3,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        zIndex: zIndexProp ?? 1300,
        transition: isDragging || isResizing ? 'none' : 'box-shadow 0.2s ease',
      }}>
        {/* Header (fullscreen'de sürüklenemez) */}
        <Box onMouseDown={isFullScreen ? undefined : handleDragStart} sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: alpha(pc, 0.02),
          cursor: isFullScreen ? 'default' : (isDragging ? 'grabbing' : 'grab'),
          userSelect: 'none', flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isFullScreen && <DragIcon sx={{ color: 'text.disabled', fontSize: 20 }} />}
            {headerContent}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {headerActions}
            <Tooltip title={isFullScreen ? 'Küçült' : 'Tam Ekran'}>
              <IconButton onClick={() => setIsFullScreen(!isFullScreen)} size="small" sx={{ color: 'text.secondary' }}>
                {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Kapat">
              <IconButton onClick={onClose} size="small"
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main,
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {children}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider',
          bgcolor: alpha(pc, 0.02), display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0,
        }}>
          {footerActions}
          {footer}
        </Box>

        {/* Resize Handle - fullscreen'de gizli */}
        {!isFullScreen && (
          <Box onMouseDown={handleResizeStart} sx={{
            position: 'absolute', right: 0, bottom: 0, width: 20, height: 20,
            cursor: 'se-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { '& svg': { color: pc } }
          }}>
            <ResizeIcon sx={{ fontSize: 16, color: 'text.disabled', transform: 'rotate(90deg)', transition: 'color 0.2s' }} />
          </Box>
        )}
      </Paper>
    </>
  );
}

DraggableResizablePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  defaultWidth: PropTypes.number,
  defaultHeight: PropTypes.number,
  primaryColor: PropTypes.string,
  headerActions: PropTypes.node,
  footerActions: PropTypes.node,
  zIndex: PropTypes.number,
  children: PropTypes.node,
};

export default React.memo(DraggableResizablePopup);
